import i18n from "i18next";
import { useEffect, useMemo, useState } from "react";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import type { StepCatalogue } from "../../../api/content/authoring/stepCatalogueApi";
import { builtInToolName, type ProcessCatalogue, type StepPorts, type StepSummary, type ToolSummary, type WorkflowDefinition } from "../../../api/content/processDefinitions";
import { readProcessItem } from "../../../api/content/processItemsApi";
import { isAbortError } from "../../../api/http/httpClient";
import { reportUiError, toUiError } from "../../../errors/uiError";
import { readToolInterface } from "../../processAuthoring/steps/useToolInterface";
import { referenceOf } from "../model/workflowDraft";
import { subWorkflowPorts, type PortCatalogue } from "../model/workflowPorts";

interface Read {
  readonly step: StepSummary | null;
  readonly workflow: WorkflowDefinition | null;
  readonly ports: StepPorts | null;
  /** For a tool step: the tool it runs, from its interface. */
  readonly tool: ToolSummary | null;
}

/**
 * Everything the graph editor draws and checks for the versions its draft pins (AU-6):
 * each step's or sub-workflow's summary, for the node, and its ports, for binding. Starts
 * from the catalogue the Workflow view already read; a version added from the palette or
 * made in the step form is read when it appears, and every one again when the person's language
 * changes. `pending` counts reads under way.
 */
export function useEditorCatalogue(draft: WorkflowContent, base: ProcessCatalogue, stepCatalogue: StepCatalogue | null): { readonly catalogue: ProcessCatalogue; readonly ports: PortCatalogue; readonly pending: number } {
  const [reads, setReads] = useState<ReadonlyMap<string, Read>>(() => new Map());
  const [requested, setRequested] = useState<ReadonlySet<string>>(() => new Set());
  // An abstract step pins nothing to read (ADR-0043).
  const wanted = useMemo(() => [...new Set(draft.steps.map(referenceOf))].filter((reference): reference is string => reference !== null && !requested.has(reference)), [draft, requested]);

  // Render-time bookkeeping: each version is requested once.
  if (wanted.length > 0) {
    setRequested(new Set([...requested, ...wanted]));
  }

  // Summaries name a Workflow in the person's language (P-21): a new language reads every
  // version again, as the base catalogue is (`useLoad`).
  useEffect(() => {
    const forget = () => {
      setReads(new Map());
      setRequested(new Set());
    };
    i18n.on("languageChanged", forget);
    return () => {
      i18n.off("languageChanged", forget);
    };
  }, []);

  useEffect(() => {
    const missing = [...requested].filter((reference) => !reads.has(reference));
    if (missing.length === 0) {
      return undefined;
    }
    const controller = new AbortController();
    for (const reference of missing) {
      // A read of a cancelled batch is never kept: one started before a language change would
      // put its words back after the change cleared them (P-21).
      readVersion(reference, stepCatalogue, controller.signal).then(
        (read) => {
          if (!controller.signal.aborted) {
            setReads((current) => new Map(current).set(reference, read));
          }
        },
        (caught: unknown) => {
          if (!controller.signal.aborted && !isAbortError(caught)) {
            reportUiError(toUiError(caught));
            setReads((current) => new Map(current).set(reference, { step: null, workflow: null, ports: null, tool: null }));
          }
        },
      );
    }
    return () => {
      controller.abort();
    };
  }, [requested, reads, stepCatalogue]);

  return useMemo(() => {
    const workflows = new Map(base.workflows);
    const steps = new Map(base.steps);
    const tools = new Map(base.tools);
    const ports = new Map<string, StepPorts>();
    for (const [reference, read] of reads) {
      if (read.step !== null) {
        steps.set(reference, read.step);
      }
      if (read.workflow !== null) {
        workflows.set(reference, read.workflow);
      }
      if (read.ports !== null) {
        ports.set(reference, read.ports);
      }
      if (read.tool !== null && !tools.has(read.tool.reference)) {
        tools.set(read.tool.reference, read.tool);
      }
    }
    const pending = [...requested].filter((reference) => !reads.has(reference)).length;
    return { catalogue: { ...base, workflows, steps, tools }, ports, pending };
  }, [base, reads, requested]);
}

/**
 * A step's or sub-workflow's summary and ports: a step's as module B serves them with its
 * version, a sub-workflow's from the steps its exports name. A pin names one exact version,
 * whose file holds the pins below it, so a chain of pins ends and needs no depth limit of
 * its own; B's own limit, `content.maximum_workflow_depth`, is its check's to report.
 */
async function readVersion(reference: string, stepCatalogue: StepCatalogue | null, signal: AbortSignal): Promise<Read> {
  const [summary, item] = await Promise.all([readProcessItem(reference, signal), readAuthoredItem(reference, signal)]);
  const content = item.content;
  if (content?.kind === "step" && summary.kind === "step") {
    const definition = content.definition;
    // An unreadable interface leaves the tool unknown; a cancelled read stays cancelled.
    const tool = definition.kind === "tool"
      ? await readToolInterface(definition.tool, stepCatalogue, signal).catch((caught: unknown) => {
          if (isAbortError(caught)) throw caught;
          return null;
        })
      : null;
    const outputs = tool === null ? null : Object.fromEntries(Object.entries(tool.outputs).map(([name, output]) => [name, output.kind]));
    const summaryOfTool: ToolSummary | null =
      definition.kind === "tool" && outputs !== null ? { reference: definition.tool, name: builtInToolName(definition.tool) ?? tool?.name ?? definition.tool, outputs, modelUse: null } : null;
    return { step: summary.step, workflow: null, ports: summary.step.ports, tool: summaryOfTool };
  }
  if (content?.kind === "workflow" && summary.kind === "workflow") {
    return { step: null, workflow: summary.workflow, ports: await workflowPortsOf(content.definition, stepCatalogue, signal), tool: null };
  }
  return { step: null, workflow: null, ports: null, tool: null };
}

async function workflowPortsOf(workflow: WorkflowContent, stepCatalogue: StepCatalogue | null, signal: AbortSignal): Promise<StepPorts> {
  const exported = new Set(Object.values(workflow.exports).map((binding) => binding.from_step));
  const inner = await Promise.all(
    workflow.steps
      .flatMap((step) => {
        const reference = referenceOf(step);
        return exported.has(step.key) && reference !== null ? [reference] : [];
      })
      .map(async (reference) => [reference, (await readVersion(reference, stepCatalogue, signal)).ports] as const),
  );
  const ports: PortCatalogue = new Map(inner.flatMap(([reference, found]) => (found === null ? [] : [[reference, found] as const])));
  return subWorkflowPorts(workflow, ports);
}
